import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Pencil, Mail, Upload, X, Camera, Share2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import EmailShareDialog from "@/components/EmailShareDialog";
import { useLang } from "@/context/LanguageContext";
import { api, API, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { shareOrDownloadFile } from "@/lib/share";

// Purely visual categories — habitable status is controlled per-room via
// the `exclude_habitable` checkbox, not by category.
const CATEGORIES = [
  { key: "habitable" },
  { key: "attic" },
  { key: "basement" },
  { key: "garage" },
  { key: "outbuilding" },
  { key: "terrace" },
  { key: "other" },
];

const emptyMeasurement = (propertyId) => ({
  property_id: propertyId,
  seller_id: "", name: "",
  length: "", width: "", area_unit: "m²",
  sections: [],
  area_manual: "",
  category: "habitable",
  exclude_habitable: false,
  measurement_date: new Date().toISOString().slice(0, 10),
  notes: "",
});

const round2 = (n) => Math.round(n * 100) / 100;

/** Human-readable dimensions: every measurement joined with "+". */
const dimsText = (r) => {
  if (r.sections?.length) return r.sections.map((s) => `${s.length} × ${s.width}`).join("  +  ");
  return r.length && r.width ? `${r.length} × ${r.width}` : "";
};

/**
 * Rooms manager scoped to a single property.
 * Rendered inside the Property edit dialog as its own tab so Julia
 * always adds/edits rooms in the context of the property they belong to.
 */
export default function PropertyRooms({ propertyId, propertyName }) {
  const { t, lang } = useLang();
  const [rooms, setRooms] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    const { data } = await api.get(`/rooms?property_id=${propertyId}`);
    setRooms(data);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    const manual = form.area_manual !== "" && form.area_manual != null ? parseFloat(form.area_manual) : null;
    // Multiple length × width measurements — each becomes a section; total is their sum
    const sections = (form.measurements || [])
      .map((r) => ({ ...r, length: parseFloat(r.length), width: parseFloat(r.width) }))
      .filter((r) => r.length > 0 && r.width > 0)
      .map((r) => ({ id: r.id, name: r.name || "", length: r.length, width: r.width, area: round2(r.length * r.width) }));
    const single = sections.length === 1 ? sections[0] : null;
    const body = {
      ...form,
      property_id: propertyId,
      property_name: propertyName || "",
      sections,
      // Legacy fields stay in sync when there is exactly one measurement
      length: single ? single.length : null,
      width: single ? single.width : null,
      area_manual: manual,
      area: manual != null && manual > 0 ? manual : round2(sections.reduce((s, x) => s + x.area, 0)),
    };
    delete body.measurements;
    try {
      if (form.id) await api.put(`/rooms/${form.id}`, body);
      else await api.post("/rooms", body);
      toast.success(t("saved"));
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/rooms/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const grouped = useMemo(() => {
    const buckets = CATEGORIES.map((c) => ({ ...c, rooms: [], total: 0 }));
    rooms.forEach((r) => {
      const cat = r.category || (r.exclude_habitable ? "other" : "habitable");
      const bucket = buckets.find((b) => b.key === cat) || buckets.find((b) => b.key === "other");
      bucket.rooms.push(r);
      bucket.total += r.area || 0;
    });
    return buckets.filter((b) => b.rooms.length > 0);
  }, [rooms]);

  const habitableTotal = rooms.reduce((s, r) => s + (r.exclude_habitable ? 0 : r.area || 0), 0);
  const excludedTotal = rooms.reduce((s, r) => s + (r.exclude_habitable ? r.area || 0 : 0), 0);

  const escapeHtml = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  const buildScheduleHtml = (withPrintScript) => {
    const dateStr = fmtDate(new Date().toISOString().slice(0, 10), lang);
    const rowsHtml = grouped.map((group) => {
      const items = group.rooms.map((r) => {
        const dims = dimsText(r);
        const excluded = r.exclude_habitable ? `<span class="excluded">${t("excludedFromHabitable")}</span>` : "";
        return `<tr>
          <td class="room">${escapeHtml(r.name)} ${excluded}</td>
          <td class="dims">${escapeHtml(dims)}</td>
          <td class="area">${(r.area ?? 0).toFixed(2)} ${r.area_unit || "m²"}</td>
        </tr>`;
      }).join("");
      return `<section>
        <div class="group-head">
          <h2>${t(`roomCategory_${group.key}`)}</h2>
        </div>
        <table><tbody>${items}</tbody></table>
      </section>`;
    }).join("");

    return `<!doctype html><html><head><meta charset="utf-8"><title>${t("roomsScheduleTitle")} — ${escapeHtml(propertyName || "")}</title>
      <style>
        body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #1c1917; margin: 0; }
        h1 { font-size: 22pt; margin: 0 0 4px; font-weight: 500; }
        .meta { font-size: 10pt; color: #78716c; margin-bottom: 24px; }
        section { margin-bottom: 20px; }
        .group-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
        .group-head h2 { font-size: 14pt; margin: 0; font-weight: 500; }
        .group-head p { margin: 0; font-size: 10pt; color: #57534e; }
        table { width: 100%; border-collapse: collapse; border: 1px solid #e7e5e4; }
        td { padding: 6px 10px; font-size: 10.5pt; border-bottom: 1px solid #f5f5f4; vertical-align: top; }
        tr:last-child td { border-bottom: none; }
        td.dims { color: #78716c; font-size: 9.5pt; white-space: nowrap; }
        td.area { text-align: right; font-weight: 500; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .excluded { display: inline-block; margin-left: 6px; padding: 1px 6px; background: #fef3c7; color: #92400e; font-size: 8.5pt; border-radius: 3px; letter-spacing: 0.05em; text-transform: uppercase; }
        .totals { margin-top: 32px; padding-top: 16px; border-top: 2px solid #1c1917; display: flex; gap: 40px; }
        .totals div p:first-child { font-size: 9pt; color: #78716c; text-transform: uppercase; letter-spacing: 0.08em; margin: 0 0 4px; }
        .totals div p:last-child { font-size: 20pt; margin: 0; font-weight: 500; font-variant-numeric: tabular-nums; }
        .excluded-total p:last-child { color: #78716c; }
        @page { margin: 15mm; }
      </style></head><body>
      <h1>${t("roomsScheduleTitle")}${propertyName ? " — " + escapeHtml(propertyName) : ""}</h1>
      <p class="meta">${dateStr}</p>
      ${rowsHtml}
      <div class="totals">
        <div><p>${t("totalHabitable")}</p><p>${habitableTotal.toFixed(2)} m²</p></div>
        ${excludedTotal > 0 ? `<div class="excluded-total"><p>${t("totalExcluded")}</p><p>${excludedTotal.toFixed(2)} m²</p></div>` : ""}
        <div><p>${t("totalArea")}</p><p>${(habitableTotal + excludedTotal).toFixed(2)} m²</p></div>
      </div>
      ${withPrintScript ? '<script>window.onload = () => { setTimeout(() => window.print(), 200); };</' + 'script>' : ""}
    </body></html>`;
  };

  const openPrint = () => {
    const win = window.open("", "_blank", "width=800,height=1000");
    if (!win) { toast.error(t("popupBlocked")); return; }
    const url = URL.createObjectURL(new Blob([buildScheduleHtml(true)], { type: "text/html" }));
    win.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div data-testid="property-rooms" className="space-y-5">
      <div className="flex items-center justify-between print:hidden">
        <p className="text-sm text-stone-500">
          {rooms.length === 0 ? t("noRoomsForProperty") : t("roomsForProperty")}
        </p>
        <div className="flex gap-2">
          {rooms.length > 0 && (
            <>
              <Button data-testid="print-property-rooms" onClick={openPrint} variant="outline" className="border-stone-300">
                <Printer size={15} className="mr-1.5" /> {t("printSchedule")}
              </Button>
              <Button data-testid="email-property-rooms" onClick={() => setEmailOpen(true)} variant="outline" className="border-stone-300">
                <Mail size={15} className="mr-1.5" /> {t("emailFiles")}
              </Button>
            </>
          )}
          <Button data-testid="add-property-room" onClick={() => setDialog(emptyMeasurement(propertyId))} className="bg-stone-900 hover:bg-stone-800 text-white">
            <Plus size={15} className="mr-1.5" /> {t("addRoom")}
          </Button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block mb-4">
        <h1 className="font-heading text-2xl font-medium text-stone-900">
          {t("roomsScheduleTitle")} — {propertyName}
        </h1>
        <p className="text-xs text-stone-500 mt-1">{fmtDate(new Date().toISOString().slice(0, 10), lang)}</p>
      </div>

      {grouped.map((group) => (
        <section key={group.key} data-testid={`property-rooms-group-${group.key}`}>
          <h3 className="font-heading text-lg font-medium text-stone-900 mb-2">{t(`roomCategory_${group.key}`)}</h3>
          <ul className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden">
            {group.rooms.map((r) => (
              <li key={r.id} data-testid={`property-room-row-${r.id}`} className="group flex items-center gap-3 px-3 py-2 hover:bg-stone-50">
                <button
                  onClick={() => setDialog({ ...r, length: r.length ?? "", width: r.width ?? "", area_manual: r.area_manual ?? "", category: r.category || "habitable", exclude_habitable: !!r.exclude_habitable })}
                  data-testid={`edit-property-room-${r.id}`}
                  className="flex-1 min-w-0 text-left flex items-center gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-900 truncate">
                      {r.name}
                      {r.exclude_habitable && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-800 bg-amber-100 rounded">
                          {t("excludedFromHabitable")}
                        </span>
                      )}
                    </p>
                    {dimsText(r) && (
                      <p className="text-[11px] text-stone-400">{dimsText(r)}</p>
                    )}
                  </div>
                  <Pencil size={13} strokeWidth={1.5} className="text-stone-300 group-hover:text-stone-600 transition-colors flex-shrink-0 print:hidden" />
                </button>
                <span className="text-sm font-medium text-stone-900 tabular-nums w-24 text-right">
                  {r.area?.toFixed(2) ?? "—"} {r.area_unit || "m²"}
                </span>
                <button
                  data-testid={`delete-property-room-${r.id}`}
                  onClick={() => remove(r.id)}
                  className="p-1 text-stone-300 hover:text-red-600 rounded transition-colors print:hidden"
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {rooms.length > 0 && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 print:bg-white print:border-stone-400 grid sm:grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-stone-500">{t("totalHabitable")}</p>
            <p className="text-2xl font-heading font-medium text-stone-900 mt-1 tabular-nums" data-testid="property-rooms-total-habitable">
              {habitableTotal.toFixed(2)} m²
            </p>
          </div>
          {excludedTotal > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-500">{t("totalExcluded")}</p>
              <p className="text-2xl font-heading font-medium text-stone-500 mt-1 tabular-nums" data-testid="property-rooms-total-excluded">
                {excludedTotal.toFixed(2)} m²
              </p>
            </div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wider text-stone-500">{t("totalArea")}</p>
            <p className="text-2xl font-heading font-medium text-stone-900 mt-1 tabular-nums" data-testid="property-rooms-total-area">
              {(habitableTotal + excludedTotal).toFixed(2)} m²
            </p>
          </div>
        </div>
      )}

      {dialog && <RoomDialog data={dialog} onClose={() => setDialog(null)} onSave={save} t={t} />}
      <EmailShareDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        contextName={propertyName || ""}
        defaultSubject={`${t("roomsScheduleTitle")}${propertyName ? " — " + propertyName : ""}`}
        getBodyHtml={async () => buildScheduleHtml(false)}
      />
    </div>
  );
}

function RoomDialog({ data, onClose, onSave, t }) {
  const makeRow = (name = "", length = "", width = "") => ({ id: crypto.randomUUID(), name, length, width });
  // Existing rooms may have sections (multi-measurement) or just legacy length/width
  const initRows = (d) => {
    if (d.sections?.length) {
      return d.sections.map((s) => ({ id: s.id || crypto.randomUUID(), name: s.name || "", length: s.length ?? "", width: s.width ?? "" }));
    }
    if (d.length && d.width) return [makeRow("", d.length, d.width)];
    return [makeRow()];
  };
  const [form, setForm] = useState({ ...data, measurements: initRows(data) });
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setForm({ ...data, measurements: initRows(data) }); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateRow = (i, patch) =>
    setForm((f) => ({ ...f, measurements: f.measurements.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addRow = () => setForm((f) => ({ ...f, measurements: [...f.measurements, makeRow()] }));
  const removeRow = (i) => setForm((f) => ({ ...f, measurements: f.measurements.filter((_, idx) => idx !== i) }));

  const rowArea = (r) => {
    const l = parseFloat(r.length);
    const w = parseFloat(r.width);
    return !isNaN(l) && !isNaN(w) && l > 0 && w > 0 ? l * w : 0;
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...(f.photos || []), up.path] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const sharePhoto = async (p, i) => {
    try {
      const r = await shareOrDownloadFile(p, `room-photo-${i + 1}.${p.split(".").pop()}`);
      if (r === "downloaded") toast.info(t("fileDownloaded"));
    } catch {
      toast.error("Share failed");
    }
  };

  const measuredTotal = (form.measurements || []).reduce((s, r) => s + rowArea(r), 0);
  const calcArea = measuredTotal > 0 ? measuredTotal : null;
  const manualArea = form.area_manual !== "" && form.area_manual != null && !isNaN(parseFloat(form.area_manual)) ? parseFloat(form.area_manual) : null;
  // Manual entry wins over the measurements total whenever it's set.
  const previewArea = manualArea != null ? manualArea.toFixed(2) : (calcArea != null ? calcArea.toFixed(2) : null);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="property-room-dialog">
        <DialogHeader><DialogTitle>{form.id ? t("editRoom") : t("addRoom")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("roomName")}</Label>
            <Input data-testid="property-room-name" value={form.name || ""} onChange={(e) => setField("name", e.target.value)} placeholder={t("roomNamePlaceholder")} />
          </div>
          <div>
            <Label>{t("roomCategory")}</Label>
            <Select value={form.category || "habitable"} onValueChange={(v) => setField("category", v)}>
              <SelectTrigger data-testid="property-room-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{t(`roomCategory_${c.key}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>{t("measurementsLabel")}</Label>
              <Select value={form.area_unit || "m²"} onValueChange={(v) => setField("area_unit", v)}>
                <SelectTrigger data-testid="property-room-unit" className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="m²">m²</SelectItem>
                  <SelectItem value="ft²">ft²</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              {(form.measurements || []).map((r, i) => (
                <div key={r.id} data-testid={`property-room-measure-row-${i}`} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg p-2">
                  <Input data-testid={`property-room-mname-${i}`} className="flex-1 min-w-[70px] h-8 text-xs" placeholder={t("sectionName")}
                    value={r.name} onChange={(e) => updateRow(i, { name: e.target.value })} />
                  <Input type="number" step="0.01" data-testid={`property-room-mlength-${i}`} className="w-20 h-8 text-xs" placeholder={t("length")}
                    value={r.length} onChange={(e) => updateRow(i, { length: e.target.value })} />
                  <span className="text-stone-400 text-xs">×</span>
                  <Input type="number" step="0.01" data-testid={`property-room-mwidth-${i}`} className="w-20 h-8 text-xs" placeholder={t("width")}
                    value={r.width} onChange={(e) => updateRow(i, { width: e.target.value })} />
                  <span data-testid={`property-room-marea-${i}`} className="text-xs font-medium text-stone-700 tabular-nums min-w-[52px] text-right">
                    {rowArea(r) > 0 ? rowArea(r).toFixed(2) : "—"}
                  </span>
                  <button type="button" data-testid={`property-room-mdel-${i}`} onClick={() => removeRow(i)}
                    disabled={(form.measurements || []).length <= 1}
                    className="p-1 text-stone-400 hover:text-red-600 disabled:opacity-30 rounded transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <button type="button" data-testid="property-room-madd" onClick={addRow}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 border border-dashed border-stone-300 hover:border-stone-400 rounded-lg px-3 py-2 transition-colors">
                <Plus size={13} strokeWidth={1.5} /> {t("addMeasurement")}
              </button>
            </div>
          </div>
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-center">
            <div className="text-xs uppercase tracking-wider text-stone-500">{t("totalArea")}</div>
            <div data-testid="property-room-total-preview" className="text-3xl font-heading font-medium text-stone-900 mt-1">
              {previewArea ? `${previewArea} ${form.area_unit || "m²"}` : <span className="text-stone-300">—</span>}
            </div>
            <div className="text-[11px] text-stone-400 mt-1">
              {manualArea != null ? t("manualOverrideActive") : t("measurementsTotalHint")}
            </div>
          </div>
          <div>
            <Label>{t("manualAreaOverride")}</Label>
            <Input
              type="number"
              step="0.01"
              data-testid="property-room-manual-area"
              value={form.area_manual ?? ""}
              onChange={(e) => setField("area_manual", e.target.value)}
              placeholder="—"
            />
            <p className="text-[11px] text-stone-400 mt-1">{t("manualAreaHint")}</p>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer bg-amber-50/40 border border-amber-100 rounded-lg p-3">
            <Checkbox
              data-testid="property-room-exclude"
              checked={!!form.exclude_habitable}
              onCheckedChange={(c) => setField("exclude_habitable", !!c)}
              className="mt-0.5"
            />
            <div className="text-sm">
              <p className="font-medium text-stone-900">{t("excludeFromHabitable")}</p>
              <p className="text-[11px] text-stone-500 mt-0.5">{t("excludeFromHabitableHint")}</p>
            </div>
          </label>
          <div>
            <Label>{t("measurementDate")}</Label>
            <Input type="date" data-testid="property-room-date" value={form.measurement_date || ""} onChange={(e) => setField("measurement_date", e.target.value)} />
          </div>
          <div>
            <Label>{t("notes")}</Label>
            <textarea rows={2} data-testid="property-room-notes" className="w-full text-sm border border-stone-200 rounded-lg p-2" value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} />
          </div>
          <div>
            <Label>{t("photos")}</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {(form.photos || []).map((p, i) => (
                <div key={p} className="relative group">
                  <img src={`${API}/files/${p}`} alt="" className="h-20 w-28 object-cover rounded-lg border border-stone-200" />
                  <button type="button" data-testid={`property-room-photo-share-${i}`} onClick={() => sharePhoto(p, i)} title={t("share")}
                    className="absolute -top-1.5 -left-1.5 bg-slate-900 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Share2 size={12} />
                  </button>
                  <button type="button" data-testid={`property-room-photo-remove-${i}`} title={t("delete")}
                    onClick={() => setForm((f) => ({ ...f, photos: (f.photos || []).filter((x) => x !== p) }))}
                    className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className={`h-20 w-28 flex flex-col items-center justify-center gap-1 border border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400 text-stone-400 hover:text-stone-600 transition-colors text-[11px] ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                <Upload size={16} strokeWidth={1.5} />
                {uploading ? "…" : t("add")}
                <input data-testid="property-room-photo-upload" type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
              </label>
              {/* Direct-to-camera on iPhone/iPad (capture is ignored on desktop) */}
              <label className={`h-20 w-28 flex flex-col items-center justify-center gap-1 border border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400 text-stone-400 hover:text-stone-600 transition-colors text-[11px] ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                <Camera size={16} strokeWidth={1.5} />
                {uploading ? "…" : t("takePhoto")}
                <input data-testid="property-room-photo-camera" type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} data-testid="property-room-cancel">{t("cancel")}</Button>
          <Button data-testid="property-room-save" disabled={!previewArea} onClick={() => onSave(form)} className="bg-stone-900 hover:bg-stone-800 text-white">{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
